import { useState } from 'react';
import { DateField } from '../../../../../../../components/general/DateField.tsx';
import { PrimaryButton } from '../../../../../../../components/general/PrimaryButton.tsx';
import { SecondaryButton } from '../../../../../../../components/general/SecondaryButton.tsx';
import { cn } from '../../../../../../../lib/utils.ts';
import type { VatReturnPeriod } from '../types/index.ts';
import {
    isSavableVatReturnPeriod,
    recentVatReturnQuarters,
    vatReturnPeriodKey,
    vatReturnVersionCount,
    type VatReturnQuarterOption,
} from '../utils/index.ts';

const QUARTERS_TO_OFFER = 3;
const PREVIOUS_QUARTER = 1;

export interface NewVatReturnPanelProps {
    today: Date;
    periods: VatReturnPeriod[];
    onGenerate: (dateFrom: string, dateTo: string) => void;
    onCancel?: () => void;
}

type ChosenPeriod = { kind: 'quarter'; quarterKey: string } | { kind: 'custom' };

function quarterKey(quarter: VatReturnQuarterOption): string {
    return vatReturnPeriodKey(quarter.dateFrom, quarter.dateTo);
}

function quarterSubtitle(quarter: VatReturnQuarterOption, periods: VatReturnPeriod[]): string {
    const savedVersions = periods.find((period) => period.key === quarterKey(quarter))?.versions.length ?? 0;
    const status = quarter.isInProgress ? `${quarter.months}, loopt nog` : quarter.months;
    if (savedVersions === 0) {
        return status;
    }
    return `${status} · ${vatReturnVersionCount(savedVersions)} opgeslagen · wordt versie ${savedVersions + 1}`;
}

export function NewVatReturnPanel({ today, periods, onGenerate, onCancel }: NewVatReturnPanelProps) {
    const quarters = recentVatReturnQuarters(today, QUARTERS_TO_OFFER);
    const [chosen, setChosen] = useState<ChosenPeriod>({ kind: 'quarter', quarterKey: quarterKey(quarters[PREVIOUS_QUARTER]) });
    const [customFrom, setCustomFrom] = useState('');
    const [customTo, setCustomTo] = useState('');

    const chosenQuarter = chosen.kind === 'quarter'
        ? quarters.find((quarter) => quarterKey(quarter) === chosen.quarterKey) ?? quarters[PREVIOUS_QUARTER]
        : undefined;
    const range = chosenQuarter
        ? { dateFrom: chosenQuarter.dateFrom, dateTo: chosenQuarter.dateTo }
        : { dateFrom: customFrom, dateTo: customTo };
    const isCustomRangeComplete = chosen.kind === 'custom' && customFrom !== '' && customTo !== '';
    const isCustomOrderInvalid = isCustomRangeComplete && customFrom.localeCompare(customTo) > 0;
    const canGenerate = range.dateFrom !== '' && range.dateTo !== '' && !isCustomOrderInvalid;

    function chooseQuarter(quarter: VatReturnQuarterOption) {
        setChosen({ kind: 'quarter', quarterKey: quarterKey(quarter) });
        setCustomFrom('');
        setCustomTo('');
    }

    function changeCustomFrom(value: string) {
        setCustomFrom(value);
        setChosen({ kind: 'custom' });
    }

    function changeCustomTo(value: string) {
        setCustomTo(value);
        setChosen({ kind: 'custom' });
    }

    return (
        <section aria-label="Nieuwe aangifte" className="mx-auto mt-10 flex w-full max-w-2xl flex-col gap-5">
            <div className="flex flex-col gap-1">
                <h2 className="text-2xl font-semibold text-gray-900">Nieuwe BTW-aangifte</h2>
                <p className="text-gray-500">
                    Kies een periode. PocketCFO maakt een concept op basis van de geboekte documenten. Sla het concept op als het klopt.
                </p>
            </div>

            <div role="radiogroup" aria-label="Kwartaal" className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
                {quarters.map((quarter) => {
                    const isChosen = chosenQuarter !== undefined && quarterKey(chosenQuarter) === quarterKey(quarter);
                    return (
                        <button
                            key={quarter.dateFrom}
                            type="button"
                            role="radio"
                            aria-checked={isChosen}
                            onClick={() => chooseQuarter(quarter)}
                            className={cn(
                                'flex flex-col items-start gap-0.5 rounded-lg border px-3.5 py-3 text-left text-gray-900',
                                isChosen ? 'border-primaryborder bg-superliteprimary' : 'border-gray-200 bg-white hover:bg-gray-50',
                            )}
                        >
                            <span className="text-base font-semibold">{quarter.label}</span>
                            <span className="text-xs text-gray-500">{quarterSubtitle(quarter, periods)}</span>
                        </button>
                    );
                })}
            </div>
            {chosenQuarter?.isInProgress && (
                <p className="text-sm text-gray-500">
                    Dit kwartaal loopt nog. Je kunt het concept bekijken en opslaan zodra het kwartaal voorbij is.
                </p>
            )}

            <fieldset className="flex flex-col gap-1">
                <legend className="text-sm text-gray-600">Of een eigen periode</legend>
                <div className="flex flex-wrap items-end gap-3">
                    <DateField
                        id="vat-return-custom-from"
                        label="Van"
                        value={customFrom}
                        onChange={changeCustomFrom}
                    />
                    <DateField
                        id="vat-return-custom-to"
                        label="Tot en met"
                        value={customTo}
                        onChange={changeCustomTo}
                    />
                </div>
                {isCustomOrderInvalid && (
                    <p role="alert" className="text-sm text-red-700">De begindatum mag niet na de einddatum liggen.</p>
                )}
                {isCustomRangeComplete && !isCustomOrderInvalid && !isSavableVatReturnPeriod(customFrom, customTo) && (
                    <p className="text-sm text-gray-500">Alleen een volledige maand of kwartaal kan worden opgeslagen.</p>
                )}
            </fieldset>

            <div className="flex gap-2">
                <PrimaryButton
                    title="Concept genereren"
                    disabled={!canGenerate}
                    onClick={() => onGenerate(range.dateFrom, range.dateTo)}
                />
                {onCancel && <SecondaryButton title="Annuleren" onClick={onCancel} />}
            </div>
        </section>
    );
}
